// One contract, so it has to be one number in one place: what this store will
// accept back is exactly what the column resizer is allowed to produce. When
// these lived here AND in table_interactions_controller.js, narrowing the
// accepted range here would have silently started dropping widths the resizer
// still handed out.
import { SELECTION_STORAGE_PREFIX } from "./table_selection_store"

export const AUTO_WIDTH_STORAGE_PREFIX = "table-interactions-auto-widths"
export const MAX_COLUMN_WIDTH = 2400
export const COLUMN_RESIZE_MIN_WIDTH = 120

export const columnSchemaSignature = (columns) =>
  columns
    .map(
      (column) => `${column.key}:${column.fixed ? "fixed" : "movable"}:${column.defaultHidden ? "hidden" : "visible"}`
    )
    .join("|")

export default class TableColumnStateStore {
  constructor({
    columns,
    defaultState,
    storageKey = null,
    legacyStorageKey = null,
    storageVersion = "v1",
    initialState,
    localStorageObject = window.localStorage,
    sessionStorageObject = window.sessionStorage
  }) {
    this.columns = columns
    this.defaultState = defaultState
    this.storageKey = storageKey
    this.legacyStorageKey = legacyStorageKey
    this.storageVersion = storageVersion
    this.initialState = initialState
    this.localStorage = localStorageObject
    this.sessionStorage = sessionStorageObject
    this.signature = columnSchemaSignature(columns)
  }

  load() {
    if (this.initialState !== undefined) return this.normalize(this.#withSchema(this.initialState))
    if (!this.storageKey) return structuredClone(this.defaultState)

    try {
      const storedState = this.localStorage.getItem(this.storageKey)
      if (storedState) return this.normalize(JSON.parse(storedState))

      const legacyState = this.legacyStorageKey && this.localStorage.getItem(this.legacyStorageKey)
      if (!legacyState) return structuredClone(this.defaultState)

      const normalized = this.normalize(JSON.parse(legacyState))
      this.#migrateLegacy(normalized)
      return normalized
    } catch (_error) {
      return structuredClone(this.defaultState)
    }
  }

  persist(state) {
    const normalized = this.normalize(this.#withSchema(state))
    if (this.initialState !== undefined || !this.storageKey) return normalized

    try {
      this.localStorage.setItem(this.storageKey, JSON.stringify(this.#withSchema(normalized)))
    } catch (_error) {
      // Keep normalized in-memory state when storage is unavailable.
    }
    return normalized
  }

  export(state) {
    return structuredClone(this.normalize(this.#withSchema(state)))
  }

  normalize(rawState = {}) {
    const defaultOrder = this.defaultState.order
    const knownKeys = new Set(defaultOrder)
    const movableKeys = this.columns.filter((column) => !column.fixed).map((column) => column.key)
    const removableKeys = new Set(
      this.columns.filter((column) => column.removable && !column.fixed).map((column) => column.key)
    )
    const schemaMatches = rawState?.version === this.storageVersion && rawState?.columnsSignature === this.signature
    const hiddenSource = schemaMatches && Array.isArray(rawState.hidden) ? rawState.hidden : this.defaultState.hidden
    const widthsSource = schemaMatches && rawState.widths && typeof rawState.widths === "object" ? rawState.widths : {}
    const orderSource = schemaMatches && Array.isArray(rawState.order) ? rawState.order : defaultOrder
    const hidden = [...new Set(hiddenSource)].filter((key) => removableKeys.has(key))
    const widths = Object.fromEntries(
      Object.entries(widthsSource).filter(([key, value]) => {
        const width = Number(value)
        return (
          movableKeys.includes(key) &&
          Number.isFinite(width) &&
          width >= COLUMN_RESIZE_MIN_WIDTH &&
          width <= MAX_COLUMN_WIDTH
        )
      })
    )
    const persistedMovableOrder = [...new Set(orderSource)].filter((key) => movableKeys.includes(key))
    const completeMovableOrder = [
      ...persistedMovableOrder,
      ...movableKeys.filter((key) => !persistedMovableOrder.includes(key))
    ]
    let movableIndex = 0
    const order = defaultOrder
      .filter((key) => knownKeys.has(key))
      .map((key) => {
        const column = this.columns.find((item) => item.key === key)
        if (column?.fixed) return key
        const nextKey = completeMovableOrder[movableIndex]
        movableIndex += 1
        return nextKey
      })
      .filter(Boolean)

    return { hidden, widths, order }
  }

  #withSchema(state) {
    return { ...state, version: this.storageVersion, columnsSignature: this.signature }
  }

  #migrateLegacy(normalized) {
    try {
      this.localStorage.setItem(this.storageKey, JSON.stringify(this.#withSchema(normalized)))
      this.localStorage.removeItem(this.legacyStorageKey)
      this.sessionStorage.removeItem(`${SELECTION_STORAGE_PREFIX}:${this.legacyStorageKey}`)
      this.sessionStorage.removeItem(
        `${AUTO_WIDTH_STORAGE_PREFIX}:${this.legacyStorageKey}:${this.storageVersion}:${this.signature}`
      )
    } catch (_error) {
      // The normalized legacy state remains usable in memory.
    }
  }
}
